import type { PileTypeId, StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import {
  isPositioned,
  pilesToPlace,
  type PieceMark,
  type PileType,
  type StructuralPiece,
} from '@obraplan/core/domain/takeoff/pile';
import type { ProjectStructureView } from '@obraplan/core/application/use-cases/structure/get-project-structure';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import {
  FLOOR_PLAN_KIND_LABEL,
  type FloorPlanKind,
} from '@obraplan/core/domain/takeoff/floor-plan';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import type { WorkspaceDraft } from './typology-draft';

/**
 * As contas da estaca na prancha, sem tela (Fase 27, etapa 1b — D4, D5).
 *
 * Moram à parte do desenho para que o que o painel diz — "faltam 58 R60", "12 de 80
 * marcadas", o próximo nome — seja um número que se testa sem montar a prancha.
 */

/**
 * As cores da estaca, na prancha e na legenda. Não seguem o tema, pelo mesmo motivo das do
 * resto do traçado: a prancha é papel branco.
 */
export const COR_DA_ESTACA = '#854d0e';
/** A estaca marcada para o serviço em escolha — o verde que a separa das outras. */
export const COR_DA_ESTACA_MARCADA = '#15803d';

/** "Ø 40 cm × 16 m" — o tipo como o projeto de fundação o escreve. */
export function descricaoDoTipo(tipo: Pick<PileType, 'diameter' | 'length'>): string {
  const diametro = Math.round(tipo.diameter * 100 * 10) / 10;
  return `Ø ${diametro.toLocaleString('pt-BR')} cm × ${tipo.length.toLocaleString('pt-BR')} m`;
}

/** Os pavimentos que ganham estaca e pilar de contenção — os que o desenho da planta atende. */
const PAVIMENTOS_DE_FUNDACAO: readonly FloorPlanKind[] = ['FUNDACAO', 'CONTENCAO'];

/**
 * Os pavimentos de fundação ou contenção que a obra tem e que ainda não têm onde desenhar.
 *
 * Sem tipologia de área comum no pavimento, não há a quem pendurar a estaca (F3, F4): a
 * prancha precisa de uma folha, e a folha é o desenho da área comum. O pavimento que a obra
 * não tem — a contenção de quem não tem arrimo — não é oferecido.
 */
export function pavimentosSemDesenho(workspace: WorkspaceDraft): readonly FloorPlanKind[] {
  return PAVIMENTOS_DE_FUNDACAO.filter(
    (kind) =>
      workspace.towers.some((torre) => torre.floors.some((andar) => andar.kind === kind)) &&
      !workspace.typologies.some(
        (tipologia) => tipologia.kind === 'AREA_COMUM' && tipologia.floorKind === kind,
      ),
  );
}

/** O pavimento da obra se chama como o tipo? "Fundação", "Contenção 2" — é o nome que se dá. */
function pavimentoDoTipo(nomeDoAndar: string, kind: FloorPlanKind): boolean {
  return normalizeName(nomeDoAndar).startsWith(normalizeName(FLOOR_PLAN_KIND_LABEL[kind]));
}

/**
 * Os andares de Fundação ou Contenção que a obra tem e que ainda não apontam desenho.
 *
 * O pavimento nasce por "Novo pavimento" com nome livre (Fase 23) e não guarda o tipo; o que
 * o diz é o nome. O desenho, quando existe, é que tem o tipo — por isso a bancada, que só
 * conhece os pavimentos já ligados a um desenho, não enxerga os que faltam.
 */
export function andaresSemDesenho(
  estrutura: ProjectStructureView | undefined,
  kind: FloorPlanKind,
): readonly { readonly id: string; readonly name: string }[] {
  return (estrutura?.tree.towers ?? [])
    .flatMap((torre) => torre.floors)
    .filter((no) => no.floor.floorPlanId === undefined && pavimentoDoTipo(no.floor.name, kind))
    .map((no) => ({ id: no.floor.id, name: no.floor.name }));
}

/**
 * Os pavimentos de Fundação ou Contenção que pedem o "Desenhar este pavimento" (F4): a obra
 * tem o andar, ou já tem o desenho dele, e ainda não há a área comum onde a estaca mora.
 */
export function pavimentosSemDesenhoDaObra({
  estrutura,
  desenhos,
  tipologias,
}: {
  estrutura: ProjectStructureView | undefined;
  desenhos: readonly { readonly kind: FloorPlanKind }[];
  tipologias: readonly Pick<WorkspaceDraft['typologies'][number], 'kind' | 'floorKind'>[];
}): readonly FloorPlanKind[] {
  return PAVIMENTOS_DE_FUNDACAO.filter(
    (kind) =>
      !tipologias.some(
        (tipologia) => tipologia.kind === 'AREA_COMUM' && tipologia.floorKind === kind,
      ) &&
      (desenhos.some((desenho) => desenho.kind === kind) ||
        andaresSemDesenho(estrutura, kind).length > 0),
  );
}

/** O serviço que conta estaca marcada: o que o catálogo aplica à ESTACA e mede por MARCADA. */
export interface ServicoMarcavel {
  /** A chave do catálogo — é por ela que a marca se guarda (`PieceMark.serviceKey`). */
  readonly key: string;
  readonly nome: string;
  readonly disciplina: string;
}

/**
 * Os serviços em que a estaca pode ser marcada: `appliesTo === 'ESTACA'` e
 * `measureBy === 'MARKED'` (D4).
 *
 * Os dois nomes chegam ao "Aplica a" por outra fatia da etapa; até lá a comparação é por
 * texto, e o catálogo que ainda não os tem simplesmente não oferece serviço nenhum.
 */
export function servicosMarcaveis(catalog: ServiceCatalog | undefined): readonly ServicoMarcavel[] {
  if (catalog === undefined) return [];
  return catalog.disciplines.flatMap((disciplina) =>
    disciplina.services
      .filter(
        (servico) =>
          (servico.appliesTo as string | undefined) === 'ESTACA' &&
          (servico.measureBy as string) === 'MARKED',
      )
      .map((servico) => ({
        key: servico.key,
        nome: servico.name,
        disciplina: disciplina.name,
      })),
  );
}

/** Quantas estacas da fila são de cada tipo — o "faltam 58 R60" do painel (D5). */
export interface FaltamDoTipo {
  readonly tipo: PileType;
  readonly quantas: number;
}

/**
 * A fila do posicionamento: as estacas sem centro, em ordem de nome, e quantas faltam de
 * cada tipo (na ordem em que os tipos foram cadastrados).
 */
export function filaDeEstacas(
  pieces: readonly StructuralPiece[],
  types: readonly PileType[],
): { readonly fila: readonly StructuralPiece[]; readonly porTipo: readonly FaltamDoTipo[] } {
  const fila = pilesToPlace(pieces);
  const porTipo = types
    .map((tipo) => ({
      tipo,
      quantas: fila.filter((estaca) => estaca.pileTypeId === tipo.id).length,
    }))
    .filter((linha) => linha.quantas > 0);
  return { fila, porTipo };
}

/** A estaca que entra depois desta na fila — a que vem sozinha quando a atual é posicionada. */
export function seguinteNaFila(
  fila: readonly StructuralPiece[],
  depoisDe: StructuralPieceId,
): StructuralPieceId | undefined {
  const indice = fila.findIndex((estaca) => estaca.id === depoisDe);
  if (indice === -1) return fila[0]?.id;
  // Dá a volta: quem pulou uma estaca para posicionar outra volta a ela no fim da fila.
  const proxima = fila[indice + 1] ?? fila.find((estaca) => estaca.id !== depoisDe);
  return proxima?.id;
}

/**
 * O próximo nome livre, na sequência do último desenhado: "E76-A" → "E77-A".
 *
 * Só o primeiro número do nome anda; o resto — a letra, o sufixo — é do projeto de fundação
 * e fica como está. Sem número no último nome, ou sem estaca nenhuma, a sugestão é "E1".
 * Pula o que já existe (sem diferenciar caixa), porque a sugestão recusada seria pior que
 * nenhuma.
 */
export function proximoNomeDeEstaca(pieces: readonly Pick<StructuralPiece, 'name'>[]): string {
  const usados = new Set(pieces.map((estaca) => estaca.name.trim().toLocaleLowerCase('pt-BR')));
  const ultimo = pieces[pieces.length - 1]?.name.trim() ?? '';
  const achado = /^(\D*)(\d+)(.*)$/s.exec(ultimo);
  const prefixo = achado?.[1] ?? 'E';
  const sufixo = achado?.[3] ?? '';
  let numero = achado === null ? 1 : Number(achado[2]) + 1;
  for (let tentativas = 0; tentativas < 10_000; tentativas += 1, numero += 1) {
    const candidato = `${prefixo}${String(numero)}${sufixo}`;
    if (!usados.has(candidato.toLocaleLowerCase('pt-BR'))) return candidato;
  }
  return `${prefixo}${String(pieces.length + 1)}${sufixo}`;
}

/** A estaca está marcada para este serviço? */
export function estacaMarcada(
  marks: readonly PieceMark[],
  pieceId: StructuralPieceId,
  serviceKey: string,
): boolean {
  return marks.some((marca) => marca.pieceId === pieceId && marca.serviceKey === serviceKey);
}

/** Quantas estacas posicionadas estão marcadas para o serviço, e quantas há no total. */
export function contagemDasMarcas(
  pieces: readonly StructuralPiece[],
  marks: readonly PieceMark[],
  serviceKey: string,
): { readonly marcadas: number; readonly posicionadas: number } {
  const posicionadas = pieces.filter(isPositioned);
  const marcadas = posicionadas.filter((estaca) =>
    estacaMarcada(marks, estaca.id, serviceKey),
  ).length;
  return { marcadas, posicionadas: posicionadas.length };
}

/** Quantas estacas posicionadas são de cada tipo — o que o filtro do "Marcar todas" mostra. */
export function posicionadasDoTipo(
  pieces: readonly StructuralPiece[],
  typeId: PileTypeId | undefined,
): number {
  return pieces.filter(
    (estaca) => isPositioned(estaca) && (typeId === undefined || estaca.pileTypeId === typeId),
  ).length;
}
